import React, {
  useCallback,
  useEffect,
  useState,
  type MutableRefObject,
  type ReactNode,
} from "react";
import "./useSections.scss";
import { fieldTypeOptions } from "../GenericField/GenericField";
import Section, { type SectionField } from "./Section/Section";

/**
 * Raw field shape as it arrives from the server inside `fields_sections`.
 * Kept loose (most members optional / `unknown`) because the same hook is
 * consumed by several endpoints whose field payloads differ slightly. Members
 * read in this file are typed; the rest are forwarded untouched.
 */
export interface RawField {
  display_name?: ReactNode;
  is_primary?: boolean;
  field_input_type?: string;
  required?: boolean;
  field_name?: string;
  // Present on dynamic-mapping default rows (read in getDefaultValue via the
  // normalized `name` carried over from getFieldData).
  name?: string;
  description?: ReactNode;
  skeleton?: boolean;
  field_input_size?: string;
  icon?: unknown;
  options?: {
    is_creatable?: boolean;
    values?: unknown;
  };
  query_data?: string;
  disabled?: boolean;
  default_value?: string | null;
  input?: string;
  mapping?: {
    rows?: { field_from?: RawField }[];
  };
  fieldDescendants?: unknown;
  fieldPrecedents?: unknown;
  mapping_data?: {
    columns?: {
      field_from?: RawField;
      field_to?: RawField;
    };
    rows?: {
      field_from?: RawField & { default_value?: string | null };
      field_to?: RawField;
    }[];
  };
}

interface RawRow {
  fields?: RawField[];
}

export interface RawSection {
  rows?: RawRow[];
  name?: string;
  display_name?: ReactNode;
  description?: ReactNode;
  is_collapsable?: boolean;
}

/**
 * A field after `getFieldData` normalization. This is the shape forwarded to a
 * consumer's `getField` callback and returned by `getFieldPropsByFieldName`.
 * Index signature keeps it assignable to the narrower per-consumer field
 * interfaces that read a subset of these members.
 */
export interface NormalizedField {
  label?: ReactNode;
  isPrimary?: boolean;
  input?: string;
  required?: boolean;
  name?: string;
  description?: ReactNode;
  isSkeleton?: boolean;
  size?: string;
  icon?: unknown;
  creatable?: boolean;
  options?: unknown;
  query?: { params?: unknown };
  disabled?: boolean;
  descendants?: unknown;
  precedents?: unknown;
  mapping?: {
    columns?: {
      field_from?: NormalizedField;
      field_to?: NormalizedField;
    };
    rows?: {
      field_from?: NormalizedField & { default_value?: string | null };
      field_to?: NormalizedField;
    }[];
  };
  defaultValue?: unknown;
  default_value?: string | null;
  [key: string]: unknown;
}

interface NormalizedRow {
  fields?: NormalizedField[];
}

export interface NormalizedSection {
  rows?: NormalizedRow[];
  name?: string;
  label?: ReactNode;
  description?: ReactNode;
  isCollapsable?: boolean;
}

/** Map of field name -> normalized default value. */
type DefaultValuesMap = Record<string, unknown>;

/** Accessor injected back into the consumer via `onGetProps`. */
export type GetFieldPropsByFieldName = (
  name?: string
) => NormalizedField | undefined;

export interface UseSectionsReturn {
  getSections: ReactNode;
  getFlatSections: () => ReactNode;
  getFieldPropsByFieldName: GetFieldPropsByFieldName;
  defaultValues: DefaultValuesMap | null;
}

const useSections = <TField extends NormalizedField = NormalizedField>(
  fieldSections?: RawSection[] | null,
  onGetProps: (getProps: () => GetFieldPropsByFieldName) => void = () => {},
  getField: (field: TField) => ReactNode = () => undefined,
  sectionsInitiallyOpened?: boolean,
  sectionsRef?: MutableRefObject<unknown[]>,
  showConfirmButton?: boolean,
  onConfirmButtonClicked: () => void = () => {},
  confirmButtonDisabled?: boolean
): UseSectionsReturn => {
  const [sections, setSections] = useState<NormalizedSection[] | null>(null);
  const [defaultValues, setDefaultValues] = useState<DefaultValuesMap | null>(
    null
  );

  const getDefaultValue = (field?: RawField): unknown => {
    if (field?.input === fieldTypeOptions.DYNAMIC_MAPPING) {
      let defaultMapping: {
        field: string | null | undefined;
        value: unknown;
      }[] = [];
      field?.mapping?.rows?.forEach((field) => {
        if (!!field?.field_from?.default_value) {
          defaultMapping.push({
            field: field?.field_from?.name,
            value: getDefaultValue(field?.field_from),
          });
        }
      });
      if (!defaultMapping.length) {
        defaultMapping.push({ field: null, value: null });
      }
      return defaultMapping;
    } else {
      try {
        const defaultValue = JSON.parse(field?.default_value as string).value;
        if (field?.input === fieldTypeOptions.NUMBER) {
          return defaultValue?.toString();
        } else if (
          field?.input === fieldTypeOptions.TEXT &&
          defaultValue === null
        ) {
          return "";
        } else if (
          field?.input === fieldTypeOptions.TAGS_LIST &&
          !defaultValue
        ) {
          return [];
        } else if (field?.input === fieldTypeOptions.HTML_TEXT_AREA) {
          return { html: defaultValue };
        } else if (
          field?.input === fieldTypeOptions.INLINE_AUTOCOMPLETE ||
          field?.input === fieldTypeOptions.AUTOCOMPLETE_ONLY_TEXTAREA
        ) {
          return { text: defaultValue };
        } else {
          return defaultValue;
        }
      } catch (e) {
        const defaultValue = field?.default_value;
        if (field?.input === fieldTypeOptions.NUMBER) {
          return defaultValue?.toString();
        } else if (
          field?.input === fieldTypeOptions.TEXT &&
          defaultValue === null
        ) {
          return "";
        } else if (
          field?.input === fieldTypeOptions.TAGS_LIST &&
          !defaultValue
        ) {
          return [];
        } else {
          return defaultValue;
        }
      }
    }
  };

  const getFieldData = (data?: RawField): NormalizedField => ({
    label: data?.display_name,
    isPrimary: data?.is_primary,
    input: data?.field_input_type,
    required: data?.required,
    name: data?.field_name,
    description: data?.description,
    isSkeleton: data?.skeleton,
    size: data?.field_input_size,
    icon: data?.icon,
    creatable: data?.options?.is_creatable,
    options: data?.options?.values,
    query: {
      ...(data?.query_data && {
        params: !!data?.query_data && JSON.parse(data?.query_data)?.params,
      }),
    },
    disabled: data?.disabled,
  });

  const getFieldPropsByFieldName = useCallback<GetFieldPropsByFieldName>(
    (name) => {
      let target: NormalizedField | undefined;
      sections?.some((section) =>
        section.rows?.some((row) =>
          row.fields?.some((field) => {
            if (field?.name === name) {
              target = field;
              return true;
            }

            field?.mapping?.rows?.some((field) => {
              if (field?.field_from?.name === name) {
                target = field?.field_from;
                return true;
              } else {
                return false;
              }
            });

            return false;
          })
        )
      );

      return target;
    },
    [sections]
  );

  useEffect(() => {
    if (!!getFieldPropsByFieldName) {
      onGetProps(() => getFieldPropsByFieldName);
    }
  }, [getFieldPropsByFieldName]);

  useEffect(() => {
    if (!!fieldSections && fieldSections?.length) {
      let defaultValuesObject: DefaultValuesMap = {};

      const sectionsArray = fieldSections?.reduce<NormalizedSection[]>(
        (sectionsAcc, section) => {
          const rows = section?.rows?.reduce<NormalizedRow[]>(
            (rowsAcc, row) => {
              const fields = row?.fields?.reduce<NormalizedField[]>(
                (fieldsAcc, field) => {
                  const fieldObject: NormalizedField = {
                    ...getFieldData(field),
                    descendants: field?.fieldDescendants,
                    precedents: field?.fieldPrecedents,
                    mapping: {
                      ...(field?.mapping_data && {
                        columns: {
                          field_from: getFieldData(
                            field?.mapping_data?.columns?.field_from
                          ),
                          field_to: getFieldData(
                            field?.mapping_data?.columns?.field_to
                          ),
                        },
                        rows: field?.mapping_data?.rows?.map((row) => {
                          return {
                            field_from: {
                              ...getFieldData(row?.field_from),
                              default_value: row?.field_from?.default_value,
                            },
                            field_to: getFieldData(row?.field_to),
                          };
                        }),
                      }),
                    },
                  };

                  fieldObject.defaultValue = getDefaultValue({
                    ...field,
                    ...fieldObject,
                  } as RawField);
                  defaultValuesObject[fieldObject?.name as string] =
                    fieldObject?.defaultValue;

                  fieldsAcc.push(fieldObject);

                  return fieldsAcc;
                },
                []
              );

              rowsAcc.push({ fields });
              return rowsAcc;
            },
            []
          );

          sectionsAcc.push({
            rows,
            name: section?.name,
            label: section?.display_name,
            description: section?.description,
            isCollapsable: section?.is_collapsable,
          });
          return sectionsAcc;
        },
        []
      );

      setDefaultValues(defaultValuesObject);
      setSections(sectionsArray);
    } else if (!!fieldSections && !fieldSections?.length) {
      setSections([]);
      setDefaultValues({});
    } else {
      setDefaultValues(null);
      setSections(null);
    }
  }, [fieldSections]);

  const getSections = (
    <ul className={"sections"}>
      {sections?.map((section, index) => (
        <li key={`section-${index}`}>
          <Section
            section={section}
            index={index}
            // `getField` accepts `TField extends NormalizedField`; Section's prop
            // type is the narrower `SectionField`. Runtime fields are normalized
            // (carry `name`/`size` + index signature) and assignable to TField.
            getField={getField as (field: SectionField) => ReactNode}
            initiallyOpen={sectionsInitiallyOpened}
            showConfirmButton={showConfirmButton}
            onConfirmButtonClicked={onConfirmButtonClicked}
            confirmButtonDisabled={confirmButtonDisabled}
            ref={(ref) => {
              // Preserves original JS behavior: assigns into `sectionsRef.current`
              // unconditionally. Consumers that render sections without passing a
              // ref (e.g. TicketEndpointFields/Keys) relied on this never being hit
              // — see refactor/bug note in the migration report.
              sectionsRef!.current[index] = ref;
            }}
          />
        </li>
      ))}
    </ul>
  );

  const getFlatSections = useCallback(
    () => (
      <>
        {sections?.flatMap((section) =>
          section?.rows?.flatMap((row) =>
            row?.fields?.map((field) => getField?.(field as TField))
          )
        )}
      </>
    ),
    [sections]
  );

  return {
    getSections,
    getFlatSections,
    getFieldPropsByFieldName,
    defaultValues,
  };
};

export default useSections;
